import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, FlaskConical, Trash2, Edit2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMAGE = '/plant-placeholder.svg';

const resolveImageUrl = (url) => {
  if (!url) return FALLBACK_IMAGE;
  if (url.startsWith('http') || url.startsWith('/plant-placeholder')) return url;
  return `${API}${url}`;
};

const TYPE_OPTIONS = [
  { value: 'indica', label: 'Indica' },
  { value: 'sativa', label: 'Sativa' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'ruderalis', label: 'Ruderalis/Auto' },
];
const TYPE_COLORS = { indica: 'badge-purple', sativa: 'badge-yellow', hybrid: 'badge-green', ruderalis: 'badge-blue' };

const GENDER_OPTIONS = [
  { value: '', label: '— Kein —' },
  { value: 'female', label: 'Weiblich' },
  { value: 'male', label: 'Männlich' },
  { value: 'unknown', label: 'Unbekannt' },
];

const GENDER_LABELS = { male: 'Männlich', female: 'Weiblich', unknown: 'Unbekannt' };

const EMPTY_FORM = {
  name: '', strain_type: 'hybrid', flowering_time: '', thc_content: '', cbd_content: '', description: '',
  parent1_id: '', parent1_gender: '', parent2_id: '', parent2_gender: '',
  breeder_id: '',
};

export default function StrainsPage() {
  const [strains, setStrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editStrain, setEditStrain] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [breeders, setBreeders] = useState([]);
  const [uploadingStrainImage, setUploadingStrainImage] = useState(false);

  useEffect(() => {
    axios.get(API + '/api/strains')
      .then(r => setStrains(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setStrains([]); })
      .finally(() => setLoading(false));
    axios.get(API + '/api/breeders')
      .then(r => setBreeders(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBreeders([]));
  }, []);

  const uploadStrainImage = async (strainId, file) => {
    setUploadingStrainImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API}/api/strains/${strainId}/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setStrains(prev => prev.map(s =>
        s.id === strainId
          ? { ...s, images: [...(s.images || []), data.url] }
          : s
      ));
    } catch (e) {
      console.error('Upload fehlgeschlagen', e);
    } finally {
      setUploadingStrainImage(false);
    }
  };

  const deleteStrainImage = async (strainId, imageUrl) => {
    await axios.delete(
      `${API}/api/strains/${strainId}/images?image_url=${encodeURIComponent(imageUrl)}`
    );
    setStrains(prev => prev.map(s =>
      s.id === strainId
        ? { ...s, images: (s.images || []).filter(u => u !== imageUrl) }
        : s
    ));
  };

  const openCreate = () => {
    setEditStrain(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditStrain(s);
    setForm({
      name: s.name,
      strain_type: s.strain_type || 'hybrid',
      flowering_time: s.flowering_time || '',
      thc_content: s.thc_content || '',
      cbd_content: s.cbd_content || '',
      description: s.description || '',
      parent1_id: s.parent1_id || '',
      parent1_gender: s.parent1_gender || '',
      parent2_id: s.parent2_id || '',
      parent2_gender: s.parent2_gender || '',
      breeder_id: s.breeder_id || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        flowering_time: form.flowering_time ? parseInt(form.flowering_time) : null,
        thc_content: form.thc_content ? parseFloat(form.thc_content) : null,
        cbd_content: form.cbd_content ? parseFloat(form.cbd_content) : null,
        parent1_id: form.parent1_id || null,
        parent1_gender: form.parent1_gender || null,
        parent2_id: form.parent2_id || null,
        parent2_gender: form.parent2_gender || null,
        breeder_id: form.breeder_id || null,
      };
      if (editStrain) {
        const { data } = await axios.put(API + '/api/strains/' + editStrain.id, payload);
        setStrains(prev => prev.map(s => s.id === data.id ? data : s));
      } else {
        const { data } = await axios.post(API + '/api/strains', payload);
        setStrains(prev => [data, ...prev]);
      }
      setShowModal(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(API + '/api/strains/' + id);
      setStrains(prev => prev.filter(s => s.id !== id));
    } catch {}
  };


  const parentOptions = strains.filter(s => !editStrain || s.id !== editStrain.id);

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="strains-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Strain-Bibliothek</h1>
          <p className="text-emerald-100/50 text-sm mt-0.5">{strains.length} Strain(s)</p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="create-strain-btn">
          <Plus size={16} /> Strain
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : strains.length === 0 ? (
        <div className="text-center py-16" data-testid="no-strains">
          <FlaskConical size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50 mb-4">Noch keine Strains in der Bibliothek</p>
          <button onClick={openCreate} className="btn-primary">Ersten Strain hinzufügen</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="strains-grid">
          {strains.map(s => (
            <div key={s.id} className="card-grow overflow-hidden" data-testid={"strain-card-" + s.id}>
              <div style={{ position: "relative", width: "100%", height: 160, borderRadius: "8px 8px 0 0", overflow: "hidden", background: "#1a1a2e" }}>
                <img
                  src={resolveImageUrl(s.images && s.images.length > 0 ? s.images[0] : null)}
                  alt={s.name}
                  onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {s.images && s.images.length > 1 && (
                  <div style={{
                    position: "absolute", bottom: 6, right: 6,
                    background: "rgba(0,0,0,0.6)", color: "#fff",
                    borderRadius: 12, padding: "2px 8px", fontSize: 11
                  }}>
                    🖼️ {s.images.length}
                  </div>
                )}
              </div>
              <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-emerald-100">{s.name}</h3>
                    <span className={"badge " + (TYPE_COLORS[s.strain_type] || 'badge-green')}>{s.strain_type}</span>
                  </div>
                  {s.description && <p className="text-sm text-emerald-100/50 mt-1 line-clamp-2">{s.description}</p>}
                  {s.breeder_name && <p className="text-xs text-emerald-100/40 mt-0.5">Breeder: <span className="text-emerald-300">{s.breeder_name}</span></p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(s)} className="p-1.5 rounded text-emerald-100/40 hover:text-emerald-400 transition-colors">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded text-emerald-100/40 hover:text-red-400 transition-colors" data-testid={"delete-strain-" + s.id}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {s.flowering_time && (
                  <div className="text-center p-2 rounded-lg" style={{ background: '#111C15', border: '1px solid #1B2A21' }}>
                    <p className="font-heading font-bold text-emerald-400 text-sm">{s.flowering_time}d</p>
                    <p className="text-xs text-emerald-100/40">Blüte</p>
                  </div>
                )}
                {s.thc_content != null && s.thc_content !== ''  && (
                  <div className="text-center p-2 rounded-lg" style={{ background: '#111C15', border: '1px solid #1B2A21' }}>
                    <p className="font-heading font-bold text-emerald-400 text-sm">{s.thc_content}%</p>
                    <p className="text-xs text-emerald-100/40">THC</p>
                  </div>
                )}
                {s.cbd_content != null && s.cbd_content !== ''  && (
                  <div className="text-center p-2 rounded-lg" style={{ background: '#111C15', border: '1px solid #1B2A21' }}>
                    <p className="font-heading font-bold text-emerald-400 text-sm">{s.cbd_content}%</p>
                    <p className="text-xs text-emerald-100/40">CBD</p>
                  </div>
                )}
              </div>
              {/* Strain Images */}
              <div className="mt-3 pt-2 border-t" style={{ borderColor: '#1B2A21' }}>
                {(s.images && s.images.length > 0) && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {s.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img src={resolveImageUrl(img)} alt={s.name} className="w-16 h-16 object-cover rounded-lg" />
                        <button
                          onClick={() => deleteStrainImage(s.id, img)}
                          className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: 'rgba(220,38,38,0.9)' }}
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {uploadingStrainImage ? (
                  <span className="animate-pulse text-emerald-400 text-xs">Uploading...</span>
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{ cursor: 'pointer', fontSize: 13, color: '#4ade80' }}>
                      📷 Foto aufnehmen
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={(e) => e.target.files[0] && uploadStrainImage(s.id, e.target.files[0])}
                      />
                    </label>
                    <label style={{ cursor: 'pointer', fontSize: 13, color: '#60a5fa' }}>
                      🖼️ Aus Galerie
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => e.target.files[0] && uploadStrainImage(s.id, e.target.files[0])}
                      />
                    </label>
                  </div>
                )}
              </div>

              {(s.parent1_id || s.parent2_id) && (
                <div className="mt-3 space-y-1 border-t pt-2" style={{ borderColor: '#1B2A21' }}>
                  {s.parent1_id && (
                    <p className="text-xs text-emerald-100/50">
                      <span className="text-emerald-100/40">Elternteil 1:</span>{' '}
                      <span className="text-emerald-300">{s.parent1_name || s.parent1_id}</span>
                      {s.parent1_gender && <span className="ml-1 text-emerald-100/40">({GENDER_LABELS[s.parent1_gender] || s.parent1_gender})</span>}
                    </p>
                  )}
                  {s.parent2_id && (
                    <p className="text-xs text-emerald-100/50">
                      <span className="text-emerald-100/40">Elternteil 2:</span>{' '}
                      <span className="text-emerald-300">{s.parent2_name || s.parent2_id}</span>
                      {s.parent2_gender && <span className="ml-1 text-emerald-100/40">({GENDER_LABELS[s.parent2_gender] || s.parent2_gender})</span>}
                    </p>
                  )}
                </div>
              )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop" data-testid="strain-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">{editStrain ? 'Strain bearbeiten' : 'Neuer Strain'}</h2>
              <button onClick={() => setShowModal(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Name *</label>
                  <input className="input-grow" placeholder="z.B. OG Kush" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="strain-name-input" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Typ</label>
                  <select className="input-grow" value={form.strain_type} onChange={e => setForm(f => ({ ...f, strain_type: e.target.value }))} data-testid="strain-type-select">
                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Blütezeit (Tage)</label>
                  <input type="number" className="input-grow" placeholder="z.B. 63" value={form.flowering_time} onChange={e => setForm(f => ({ ...f, flowering_time: e.target.value }))} data-testid="strain-flowering-input" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">THC %</label>
                  <input type="number" step="0.1" className="input-grow" placeholder="z.B. 22" value={form.thc_content} onChange={e => setForm(f => ({ ...f, thc_content: e.target.value }))} data-testid="strain-thc-input" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">CBD %</label>
                  <input type="number" step="0.1" className="input-grow" placeholder="z.B. 1" value={form.cbd_content} onChange={e => setForm(f => ({ ...f, cbd_content: e.target.value }))} data-testid="strain-cbd-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Breeder</label>
                <select className="input-grow" value={form.breeder_id} onChange={e => setForm(f => ({ ...f, breeder_id: e.target.value }))} data-testid="strain-breeder-select">
                  <option value="">— Kein Breeder —</option>
                  {breeders.map(b => <option key={b.id} value={b.id}>{b.name}{b.country ? ` (${b.country})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Beschreibung</label>
                <textarea className="input-grow" rows={3} placeholder="Eigenschaften, Geschmack, Wirkung..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="strain-desc-input" />
              </div>

              <div className="border-t pt-3" style={{ borderColor: '#1B2A21' }}>
                <p className="text-xs text-emerald-100/60 mb-2 uppercase tracking-wider font-semibold">Elternteile (optional)</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-emerald-100/60 mb-1">Elternteil 1 – Strain</label>
                    <select className="input-grow" value={form.parent1_id} onChange={e => setForm(f => ({ ...f, parent1_id: e.target.value }))} data-testid="strain-parent1-select">
                      <option value="">— Kein —</option>
                      {parentOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-emerald-100/60 mb-1">Elternteil 1 – Geschlecht</label>
                    <select className="input-grow" value={form.parent1_gender} onChange={e => setForm(f => ({ ...f, parent1_gender: e.target.value }))} data-testid="strain-parent1-gender-select">
                      {GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-emerald-100/60 mb-1">Elternteil 2 – Strain</label>
                    <select className="input-grow" value={form.parent2_id} onChange={e => setForm(f => ({ ...f, parent2_id: e.target.value }))} data-testid="strain-parent2-select">
                      <option value="">— Kein —</option>
                      {parentOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-emerald-100/60 mb-1">Elternteil 2 – Geschlecht</label>
                    <select className="input-grow" value={form.parent2_gender} onChange={e => setForm(f => ({ ...f, parent2_gender: e.target.value }))} data-testid="strain-parent2-gender-select">
                      {GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-strain-btn">
                  {saving ? 'Speichern...' : editStrain ? 'Aktualisieren' : 'Hinzufügen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
